// v2-only fork of ../components/ChartRenderer.jsx — kept separate from v1's
// (now-unrouted) copy; this one adds row-filtering for merged multi-source
// datasets (see filterChartRows below) and is the version served at /ask-psd.

import React from 'react';
import ReactEcharts from 'echarts-for-react';
import { getChartColor } from '../components/chartTheme';
import { parseTemporalValue } from '../services/responseNormalizer';

const formatNumber = (value) => {
  if (typeof value !== 'number') {
    return value;
  }

  return new Intl.NumberFormat('en', {
    notation: value >= 1000000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value);
};

const formatMeasure = (value, valueFormat) => {
  if (typeof value !== 'number') {
    return value;
  }

  if (valueFormat === 'hours') {
    return `${formatNumber(value)} h`;
  }

  return formatNumber(value);
};

const sortDataByXAxis = (data, xaxis, xaxisType) => {
  if (!xaxis || xaxisType !== 'time') {
    return data;
  }

  return [...data].sort((left, right) => {
    const leftValue = parseTemporalValue(left[xaxis]);
    const rightValue = parseTemporalValue(right[xaxis]);

    if (leftValue === null || rightValue === null) {
      return 0;
    }

    return leftValue - rightValue;
  });
};

// "Full dashboard" style questions merge rows from several unrelated
// sources (e.g. FillRate rows with no userName mixed with TopUsers rows) —
// without this, those unrelated rows show up as a stray "undefined" bar/
// point on charts whose x-axis is a field only some rows actually have.
const isUsableValue = (value) => value !== undefined && value !== null && value !== '';

const filterChartRows = (data, xAxis, yAxis) =>
  data.filter((row) => isUsableValue(row[xAxis]) && isUsableValue(row[yAxis]));

function pivotData(data, xAxis, groupBy, yAxis) {
  const result = {};

  data.forEach((row) => {
    const x = row[xAxis];
    const group = row[groupBy];

    if (!isUsableValue(x) || !isUsableValue(group)) {
      return;
    }

    if (!result[x]) {
      result[x] = { [xAxis]: x };
    }

    result[x][group] = row[yAxis];
  });

  return Object.values(result);
}

const getGroups = (data, groupBy) => [
  ...new Set(
    data.map((row) => row[groupBy]).filter((value) => value !== undefined && value !== null && value !== '')
  ),
];

const baseOption = (tooltipFormatter) => ({
  tooltip: {
    trigger: 'axis',
    valueFormatter: tooltipFormatter,
  },
  grid: { left: 48, right: 24, top: 56, bottom: 56, containLabel: true },
});

const legendOption = (data) => ({
  type: 'scroll',
  top: 4,
  data,
});

export default function ChartRenderer({ response }) {
  const chartType = response.chartType;
  const chartConfig = response.chartConfig;
  const rawData = response.chartData || response.data;

  if (!rawData?.length || !chartConfig?.xaxis || !chartConfig?.yaxis) {
    return null;
  }

  const data = sortDataByXAxis(rawData, chartConfig.xaxis, chartConfig.xaxisType);
  const chartTitle = chartConfig.title || 'Visualization';
  const chartSubtitle = chartConfig.description || `${data.length} rows returned`;
  const tooltipFormatter = (value) => formatMeasure(value, chartConfig.valueFormat);
  const yTickFormatter = (value) => formatMeasure(value, chartConfig.valueFormat);

  const renderChart = (option, height = 440) => (
    <section className="psd-card chart-card">
      <div className="section-header">
        <div>
          <p className="section-kicker">Visualization</p>
          <h2>{chartTitle}</h2>
        </div>
        <span className="row-count">{chartSubtitle}</span>
      </div>

      <div className="chart-frame">
        <ReactEcharts option={option} notMerge={true} style={{ height }} />
      </div>
    </section>
  );

  if (chartType === 'BAR') {
    const rows = filterChartRows(data, chartConfig.xaxis, chartConfig.yaxis);

    if (!rows.length) {
      return null;
    }

    const categories = rows.map((row) => row[chartConfig.xaxis]);
    const values = rows.map((row) => row[chartConfig.yaxis]);

    return renderChart({
      ...baseOption(tooltipFormatter),
      // Long category names (e.g. full user names) get silently skipped by
      // ECharts' default label-overlap avoidance when horizontal — rotating
      // them lets more/all labels actually render instead of looking like
      // most bars have no name.
      xAxis: { type: 'category', data: categories, axisTick: { show: false }, axisLabel: { rotate: 28 } },
      yAxis: { type: 'value', axisLabel: { formatter: yTickFormatter } },
      series: [
        {
          type: 'bar',
          data: values,
          barMaxWidth: 56,
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: (params) => getChartColor(params.dataIndex),
          },
        },
      ],
    });
  }

  if (chartType === 'LINE') {
    const hasGrouping = chartConfig.groupBy && getGroups(data, chartConfig.groupBy).length > 0;

    if (hasGrouping) {
      const groupedData = pivotData(data, chartConfig.xaxis, chartConfig.groupBy, chartConfig.yaxis);
      const groups = getGroups(data, chartConfig.groupBy);

      if (!groupedData.length || !groups.length) {
        return null;
      }

      const categories = groupedData.map((row) => row[chartConfig.xaxis]);

      return renderChart({
        ...baseOption(tooltipFormatter),
        legend: legendOption(groups),
        xAxis: { type: 'category', data: categories, axisTick: { show: false } },
        yAxis: { type: 'value', axisLabel: { formatter: yTickFormatter } },
        color: groups.map((_, index) => getChartColor(index)),
        series: groups.map((group) => ({
          type: 'line',
          name: group,
          data: groupedData.map((row) => row[group] ?? null),
          smooth: true,
          connectNulls: true,
        })),
      });
    }

    const rows = filterChartRows(data, chartConfig.xaxis, chartConfig.yaxis);

    if (!rows.length) {
      return null;
    }

    const categories = rows.map((row) => row[chartConfig.xaxis]);
    const values = rows.map((row) => row[chartConfig.yaxis]);

    return renderChart({
      ...baseOption(tooltipFormatter),
      legend: legendOption([chartConfig.yaxis]),
      xAxis: { type: 'category', data: categories, axisTick: { show: false } },
      yAxis: { type: 'value', axisLabel: { formatter: yTickFormatter } },
      color: [getChartColor(0)],
      series: [
        {
          type: 'line',
          name: chartConfig.yaxis,
          data: values,
          smooth: true,
          connectNulls: true,
        },
      ],
    });
  }

  if (chartType === 'GROUPED_BAR') {
    const transformed = pivotData(data, chartConfig.xaxis, chartConfig.groupBy, chartConfig.yaxis);
    const groups = getGroups(data, chartConfig.groupBy);

    if (!transformed.length || !groups.length) {
      return null;
    }

    const categories = transformed.map((row) => row[chartConfig.xaxis]);

    return renderChart({
      ...baseOption(tooltipFormatter),
      legend: legendOption(groups),
      xAxis: { type: 'category', data: categories, axisLabel: { rotate: 28 } },
      yAxis: { type: 'value', axisLabel: { formatter: yTickFormatter } },
      color: groups.map((_, index) => getChartColor(index)),
      series: groups.map((group) => ({
        type: 'bar',
        name: group,
        data: transformed.map((row) => row[group] ?? null),
        barMaxWidth: 34,
        itemStyle: { borderRadius: [5, 5, 0, 0] },
      })),
    });
  }

  return null;
}
